import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { useApi } from '@fnol/library/src/hooks';
import {
    question50MaxLength,
    requestType,
    translations,
    question20MaxLength,
    chasisMaxLength,
    chasisFreeMaxLength,
} from '../../lib/consts';
import { IVehicleType, prepareVehicleTypesList } from '../../lib/helpers';
import { getVehicleTypes } from '../../rpc/actions';
import VehicleOwner from '../VehicleOwner';
import VehicleUser from '../VehicleUser';
import DriverSection from '../Driver/DriverSection';
import DamagedVehicleSection from '../DamagedVehicleSection';

type GenericObject = { [key: string]: any };

const DamageVehicle = () => {
    const { values, errors, touched, setFieldValue } = useFormikContext<any>();
    const { response: vehicleTypes, fetchMethod: fetchVehicleTypes } = useApi(getVehicleTypes);
    const [preparedVehicleTypes, setPreparedVehicleTypes] = useState<any>();

    const type = 'vehicleType';
    const chassisNumber = 'chassisNumber';
    const brand = 'vehicleBrand';
    const model = 'vehicleModel';
    const registration = 'vehicleReg';
    const year = 'vehicleProductionYear';

    useEffect(() => {
        fetchVehicleTypes();
        setFieldValue('showVehicleDamageForm', true);
        checkIfChassisNumberExists(values[chassisNumber]);
    }, []);

    useEffect(() => {
        Array.isArray(vehicleTypes?.data?.data) &&
            setPreparedVehicleTypes(prepareVehicleTypesList(vehicleTypes.data.data));
    }, [vehicleTypes]);

    useEffect(() => {
        const materialDamage =
            !!values[chassisNumber] &&
            !values.showVehicleDamageForm &&
            values.requestType === requestType.MaterialDamage;
        setFieldValue('materialDamage', materialDamage);

        const immaterialOrBothDamage =
            !!values[chassisNumber] && !values.showVehicleDamageForm && values.requestType === requestType.Both;
        setFieldValue('immaterialOrBothDamage', immaterialOrBothDamage);
    }, [values[chassisNumber], values.showVehicleDamageForm]);

    const handleChangeVehicleType = (e: { target: { value: string } }) => {
        setFieldValue(type, e.target.value);
    };

    const checkIfChassisNumberExists = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const filteredClaims = values.reportedClaims?.filter((claim: GenericObject) => {
            if (claim.nematerijalna === false && claim.brojSasije !== '')
                return claim.brojSasije === values[chassisNumber];
        });

        setFieldValue('showVehicleDamageForm', filteredClaims?.length === 0);
    };

    const handleInputYear = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.value = parseInt(e.target.value).toString().slice(0, 4);
    };

    const numberInputOnWheelPreventChange = (e: React.WheelEvent<HTMLElement>) => {
        (e.target as HTMLElement).blur();
        e.stopPropagation();
        setTimeout(() => {
            (e.target as HTMLElement).focus();
        }, 0);
    };

    return (
        <>
            <FormControl>
                <InputLabel id="vehicleType-label">{translations.vehicle.TYPE}</InputLabel>

                <Select
                    id={type}
                    labelId="vehicleType-label"
                    name={type}
                    value={values[type] || ''}
                    label={translations.vehicle.TYPE}
                    error={!!(touched[type] && errors[type])}
                    onChange={handleChangeVehicleType}
                >
                    {preparedVehicleTypes?.map((option: IVehicleType) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.text}
                            </MenuItem>
                        );
                    })}
                </Select>
                {touched[type] && errors[type] ? (
                    <FormHelperText error={true}>
                        <>{errors[type]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <Tooltip
                title={
                    [1, 2, 3, 4, 6, 7].includes(values.vehicleType) ? <p>{translations.tooltips.CHASSIS_NUMBER}</p> : ''
                }
                placement="top-start"
            >
                <FormControl>
                    <Field
                        name={chassisNumber}
                        label={translations.vehicle.CHASSIS_NUMBER}
                        as={TextField}
                        onBlur={checkIfChassisNumberExists}
                        required={true}
                        inputProps={
                            [1, 2, 3, 4, 6, 7].includes(values.vehicleType)
                                ? { maxLength: chasisMaxLength }
                                : { maxLength: chasisFreeMaxLength }
                        }
                        error={!!(touched[chassisNumber] && errors[chassisNumber])}
                    />
                    {touched[chassisNumber] && errors[chassisNumber] ? (
                        <FormHelperText error={true}>
                            <>{errors[chassisNumber]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            </Tooltip>

            {values.showVehicleDamageForm && (
                <>
                    <FormControl>
                        <Field
                            name={brand}
                            label={translations.vehicle.BRAND}
                            as={TextField}
                            inputProps={{
                                maxLength: question50MaxLength,
                            }}
                            required={true}
                            error={!!(touched[brand] && errors[brand])}
                        />
                        {touched[brand] && errors[brand] ? (
                            <FormHelperText error={true}>
                                <>{errors[brand]}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>

                    <FormControl>
                        <Field
                            name={model}
                            label={translations.vehicle.MODEL}
                            as={TextField}
                            inputProps={{
                                maxLength: question50MaxLength,
                            }}
                            required={true}
                            error={!!(touched[model] && errors[model])}
                        />
                        {touched[model] && errors[model] ? (
                            <FormHelperText error={true}>
                                <>{errors[model]}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>

                    <Tooltip title={translations.tooltips.REGISTRATION_NUMBER_TOOLTIP} placement="top-start">
                        <FormControl>
                            <Field
                                name={registration}
                                label={translations.vehicle.REGISTRATION}
                                as={TextField}
                                inputProps={{
                                    maxLength: question20MaxLength,
                                }}
                                required={true}
                                error={!!(touched[registration] && errors[registration])}
                            />
                            {touched[registration] && errors[registration] ? (
                                <FormHelperText error={true}>
                                    <>{errors[registration]}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </Tooltip>

                    <FormControl>
                        <Field
                            name={year}
                            label={translations.vehicle.PRODUCTION_YEAR}
                            inputProps={{ min: 0 }}
                            type="number"
                            as={TextField}
                            required={true}
                            onInput={handleInputYear}
                            onWheel={numberInputOnWheelPreventChange}
                            error={!!(touched[year] && errors[year])}
                        />
                        {touched[year] && errors[year] ? (
                            <FormHelperText error={true}>
                                <>{errors[year]}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                    <VehicleOwner />
                    <VehicleUser />
                    <DriverSection />
                    <DamagedVehicleSection />
                </>
            )}

            {!!values.materialDamage && (
                <Stack direction="row" alignItems="top" spacing={2} marginTop={1}>
                    <Info color="primary" />
                    <Typography variant="body2" paddingTop={0.5} color={'#c21b17'}>
                        {translations.validationMessages.CLAIM_ALREADY_EXISTS_FOR_CHASSIS_NUMBER_MATERIAL}
                    </Typography>
                </Stack>
            )}
            {!!values.immaterialOrBothDamage && (
                <Stack direction="row" alignItems="top" spacing={2} marginTop={1}>
                    <Info color="primary" />
                    <Typography variant="body2" paddingTop={0.5} color={'#c21b17'}>
                        {translations.validationMessages.CLAIM_ALREADY_EXISTS_FOR_CHASSIS_NUMBER_IMMATERIAL}
                    </Typography>
                </Stack>
            )}
            {values.chassisNumber === values.chassisNumberByPolicy &&
            values.requestType === requestType.MaterialDamage ? (
                <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                    <Info color="primary" />
                    <Typography variant="body2" paddingTop={0.5}>
                        {translations.validationMessages.CANNOT_USE_POLICY}
                    </Typography>
                </Stack>
            ) : null}
            {values.chassisNumber === values.chassisNumberByPolicy && values.requestType === requestType.Both ? (
                <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                    <Info color="primary" />
                    <Typography variant="body2" paddingTop={0.5}>
                        {translations.validationMessages.CANNOT_USE_POLICY_BY_CAR}
                    </Typography>
                </Stack>
            ) : null}
        </>
    );
};

export default DamageVehicle;
